import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import * as IconMUI from '@mui/icons-material';
import useStyles from './styles';
import { SubmissionStatus as SubmissionStatusType } from 'types/submissions';
import { OrderStatus } from '@elevator/api-hooks/types/schema-types';

/**
 * Props for the SubmissionStatus component.
 */
export interface SubmissionStatusProps {
  /** The submission status variant (Review, Approval, Approved, Rejected, Completed, or Flagged). */
  variant: SubmissionStatusType | 'Flagged';
  /** Optional order status used to determine indicator appearance for Flagged variants. */
  orderStatus?: OrderStatus;
  /** Whether the order has been flagged for review or issues. */
  orderFlagged?: boolean;
}

const getTooltipText = (
  variant: SubmissionStatusType | 'Flagged',
  orderStatus?: OrderStatus,
  orderFlagged?: boolean,
): string => {
  switch (variant) {
    case 'Review':
    case 'Approval':
      return `Your submission is being reviewed by an Elevator Staff member. Due to the high volume of submissions, we are unable to respond to every request. We review most submissions within 72 hours but may take up to 7 days.`;
    case 'Flagged':
      if (orderStatus === 'failed' || orderFlagged === true) {
        return `An issue occurred while processing your payment. Please create a new submission and try again.`;
      }
      return `Your submission has been flagged and is awaiting further review from an Elevator staff member.`;
    case 'Completed':
      return `Your submission has been posted on the Elevator site! Be sure to share the news with your followers on social media.`;
    case 'Rejected':
      return `Your submission has been declined and will not be posted on the Elevator site.`;
    case 'Approved':
      return `Your submission has been approved and is in the process of being published on our site.`;
    default:
      return `Your submission status is being processed.`;
  }
};

/**
 * SubmissionStatus Component
 *
 * Displays a status indicator with an icon and text label representing the submission status.
 * Shows a tooltip with explanatory text when hovered. For Flagged variants,
 * the indicator appearance changes based on order status or flagged state.
 *
 * Used in:
 * - Submission layout (view mode)
 * - SubmissionCard (submission list)
 * - OrderDetails (alongside order information)
 */
const SubmissionStatus = ({
  variant,
  orderStatus,
  orderFlagged,
}: SubmissionStatusProps) => {
  const { classes } = useStyles();

  const getFlaggedContent = (): { text: string; icon: React.ReactElement } => {
    if (orderStatus && (orderStatus === 'failed' || orderFlagged)) {
      return {
        text: 'Payment Issue',
        icon: <IconMUI.MoneyOff className={classes.rejectedIcon} />,
      };
    }
    return {
      text: 'Action',
      icon: <IconMUI.FlagCircle className={classes.flaggedIcon} />,
    };
  };

  const getPendingContent = (): { text: string; icon: React.ReactElement } => ({
    text: 'Pending',
    icon: <IconMUI.Pending className={classes.pendingIcon} />,
  });

  const getStatusContent = (): {
    text: string;
    icon: React.ReactElement;
  } => {
    if (variant === 'Flagged') {
      return getFlaggedContent();
    }
    if (variant === 'Review' || variant === 'Approval') {
      return getPendingContent();
    }

    const statusMap: Record<
      Exclude<SubmissionStatusType, 'Review' | 'Approval'>,
      { text: string; icon: React.ReactElement }
    > = {
      Approved: {
        text: 'Approved',
        icon: <IconMUI.CheckCircle className={classes.approvedIcon} />,
      },
      Rejected: {
        text: 'Declined',
        icon: <IconMUI.NotInterested className={classes.rejectedIcon} />,
      },
      Completed: {
        text: 'Published',
        icon: <IconMUI.Public className={classes.approvedIcon} />,
      },
    };

    return statusMap[variant] ?? getPendingContent();
  };

  const { text: statusText, icon: statusIcon } = getStatusContent();
  const tooltipTitle = getTooltipText(variant, orderStatus, orderFlagged);

  return (
    <Tooltip title={tooltipTitle} enterNextDelay={300}>
      <Stack
        className={classes.root}
        direction="row"
        spacing={1}
        alignItems="center"
        data-component="SubmissionStatus"
      >
        {statusIcon}
        <Typography variant="body2" className={classes.statusText}>
          {statusText}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

export default SubmissionStatus;
